import React, { useRef, useState } from 'react';
import { Presentation, Download, FileSpreadsheet, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { capUpdateProject, capGenerateDeck, capListDecks, capDownload } from '../../lib/capstoneApi';
import { Field, inputCls, btnAmber, btnGhost, fmtDate } from './shared';
import GuidePanel from './GuidePanel';
import RefineBar from './RefineBar';

const STYLE_OPTIONS = [
  ['corporate', 'Corporate / McKinsey-style', 'Navy & teal, editorial serif headings'],
  ['academic', 'Academic', 'Deep green, Cambria, formal tone'],
  ['minimal', 'Minimal', 'Black & gold, maximum whitespace'],
];

const TEXT_FIELDS = [
  ['methodology', 'Methodology / approach', 'methodology', 'How did you investigate — methods, data, tools?'],
  ['findings', 'Key findings / analysis', 'findings', 'The headline insights your analysis produced (one per line works well)'],
  ['recommendations_text', 'Recommendations / solution', 'recommendations', 'What should the sponsor do? One recommendation per line'],
];

export default function ExportTab({ project, docs, reloadProject }) {
  const [texts, setTexts] = useState({
    methodology: project.methodology || '', findings: project.findings || '',
    recommendations_text: project.recommendations_text || '', exec_summary: project.exec_summary || '',
  });
  const timers = useRef({});
  const [showModal, setShowModal] = useState(false);
  const [style, setStyle] = useState('corporate');
  const [featured, setFeatured] = useState([]);
  const [modalExec, setModalExec] = useState('');
  const [generating, setGenerating] = useState(false);
  const [decks, setDecks] = useState(null);
  const imageDocs = docs.filter((d) => d.is_image);

  const loadDecks = () => capListDecks(project.id).then((r) => setDecks(r.decks)).catch(() => {});
  if (decks === null) loadDecks();

  const save = (field, value, origin) => {
    setTexts((t) => ({ ...t, [field]: value }));
    clearTimeout(timers.current[field]);
    timers.current[field] = setTimeout(async () => {
      try {
        const payload = { [field]: value };
        if (origin) payload.authorship = { [field]: origin };
        await capUpdateProject(project.id, payload);
      } catch { toast.error('Autosave failed'); }
    }, 900);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await capGenerateDeck(project.id, { style, exec_summary: modalExec || texts.exec_summary, featured_doc_ids: featured });
      toast.success(`Presentation v${res.version} generated`);
      setShowModal(false);
      loadDecks();
      await capDownload(`/capstone/decks/${res.deck_id}/download`, 'Capstone_Presentation.pptx');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1.4fr,1fr] gap-7" data-testid="cap-export-tab">
      <div className="space-y-6">
        <RefineBar
          getFields={() => texts}
          context={{ title: project.title, domain: project.domain, sponsor_org: project.sponsor_org }}
          onApply={(f) => Object.entries(f).forEach(([k, v]) => save(k, v, 'ai_edited'))}
          label="Refine my report inputs to CXO-ready language"
        />
        <div className="text-[13px] text-white/55 leading-relaxed">
          Everything below flows straight into your final presentation — write it once, generate as many deck versions as you like.
          <span className="text-emerald-300/70 text-[11px] ml-1.5 italic">Autosaves as you type.</span>
        </div>
        {TEXT_FIELDS.map(([field, label, topic, ph]) => (
          <div key={field}>
            <Field label={label}>
              <textarea rows={4} className={`${inputCls} resize-y`} placeholder={ph} value={texts[field]}
                onChange={(e) => save(field, e.target.value)} data-testid={`cap-export-${field}`} />
            </Field>
            <GuidePanel
              topic={topic}
              projectId={project.id}
              onUse={(text, origin) => { save(field, origin === 'ai' ? text : (texts[field] ? texts[field] + '\n' + text : text), origin); toast.success(origin === 'ai' ? 'Applied' : 'Loaded as a starting point'); }}
            />
          </div>
        ))}
        <div>
          <Field label="Executive summary (2-3 sentences)">
            <textarea rows={3} className={`${inputCls} resize-y`} placeholder="Left blank? We auto-summarize from your charter." value={texts.exec_summary}
              onChange={(e) => save('exec_summary', e.target.value)} data-testid="cap-export-exec-summary" />
          </Field>
          <GuidePanel topic="exec_summary" projectId={project.id}
            onUse={(text, origin) => { save('exec_summary', text, origin); toast.success('Executive summary applied'); }} />
          <GuidePanel topic="ppt_narrative" projectId={project.id} label="Ask Guide for a slide-by-slide narrative"
            onUse={(text) => { navigator.clipboard?.writeText(text); toast.success('Narrative copied to clipboard'); }} />
        </div>
      </div>

      <div className="space-y-5">
        <div className="rounded-2xl border border-amber-300/25 bg-amber-300/[0.04] p-5">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-amber-300 mb-2">Capstone presentation</div>
          <p className="text-[12px] text-white/55 leading-relaxed mb-4">A polished 10-12 slide .pptx auto-built from your charter, milestones, findings, risks and mentor feedback. Fully editable in PowerPoint.</p>
          <button className={`${btnAmber} w-full justify-center`} onClick={() => { setModalExec(texts.exec_summary); setShowModal(true); }} data-testid="cap-generate-deck-btn">
            <Presentation className="w-4 h-4" /> Generate Capstone Presentation
          </button>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-5">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/50 mb-3">Deck version history</div>
          {(decks || []).length === 0 && <div className="text-[12px] text-white/35 italic">No decks generated yet.</div>}
          {(decks || []).map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-2 border-b border-white/6 last:border-0" data-testid={`cap-deck-row-${d.id}`}>
              <Presentation className="w-4 h-4 text-amber-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-white/80">v{d.version} · {d.style}</div>
                <div className="text-[10px] text-white/35">{d.created_by} · {fmtDate(d.created_at)} · {(d.size / 1024).toFixed(0)} KB</div>
              </div>
              <button className="p-1.5 text-white/45 hover:text-amber-300" data-testid={`cap-deck-download-${d.id}`}
                onClick={() => capDownload(`/capstone/decks/${d.id}/download`, `Capstone_v${d.version}.pptx`).catch(() => toast.error('Download failed'))}>
                <Download className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <button className={`${btnGhost} w-full justify-center`} data-testid="cap-excel-export-btn"
          onClick={() => capDownload(`/capstone/projects/${project.id}/export.xlsx`, 'Capstone_log.xlsx').then(() => toast.success('Excel export downloaded')).catch(() => toast.error('Export failed'))}>
          <FileSpreadsheet className="w-4 h-4" /> Export full project log (Excel)
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="cap-deck-modal">
          <div className="w-full max-w-lg rounded-2xl border border-white/12 bg-[#0c1524] p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="font-serif text-xl">Generate presentation</div>
              <button onClick={() => setShowModal(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <Field label="Executive summary (optional — auto-summarized if blank)">
              <textarea rows={3} className={`${inputCls} resize-y`} value={modalExec} onChange={(e) => setModalExec(e.target.value)} data-testid="cap-modal-exec-input" />
            </Field>
            <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/50 mb-2 mt-5">Template style</div>
            <div className="space-y-2">
              {STYLE_OPTIONS.map(([key, name, desc]) => (
                <button key={key} onClick={() => setStyle(key)} data-testid={`cap-style-${key}`}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${style === key ? 'border-amber-300 bg-amber-300/8' : 'border-white/12 hover:border-white/30'}`}>
                  <div className="text-[13px] font-semibold text-white/85">{name}</div>
                  <div className="text-[10.5px] text-white/40">{desc}</div>
                </button>
              ))}
            </div>
            {imageDocs.length > 0 && (
              <>
                <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/50 mb-2 mt-5">Feature up to 3 charts / images</div>
                <div className="space-y-1.5">
                  {imageDocs.map((d) => (
                    <label key={d.id} className="flex items-center gap-2.5 text-[12.5px] text-white/70 cursor-pointer">
                      <input type="checkbox" checked={featured.includes(d.id)}
                        onChange={(e) => setFeatured((f) => e.target.checked ? [...f, d.id].slice(0, 3) : f.filter((x) => x !== d.id))}
                        className="accent-amber-300" />
                      {d.name} <span className="text-white/30">v{d.version}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <button className={`${btnAmber} w-full justify-center mt-6`} disabled={generating} onClick={generate} data-testid="cap-modal-generate-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Build & download .pptx
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
